/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  ProgressBar,
  Badge,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { supabase } from '../lib/supabase';
// Assuming this component handles the colored progress bars
import AssessmentScoreBar from '../components/assessment/AssessmentScoreBar';

// 1. Updated Interfaces to match your JSON structure
interface SectionPerformance {
  title: string;
  average_percentage: number;
}

interface ProfileData {
  profile: {
    id: string;
    is_published: boolean;
    bio: string | null;
    contact_email: string | null;
    organisations: {
      name: string;
      website: string | null;
      registration_number: string;
      organisation_type: string;
    };
  };
  compliance: {
    average_compliance_percentage: number;
    assessment_count: number;
    file_overview: {
      completion_percentage: number;
    };
    section_performance: {
      strong_sections: SectionPerformance[];
      weak_sections: SectionPerformance[];
    };
  };
}

const SupplierProfile = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('is_preview') === 'true';
  const [data, setData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const { data: response, error: funcError } =
          await supabase.functions.invoke('get-supplier-profile-view', {
            body: { slug, is_preview: isPreview },
          });

        if (funcError) throw funcError;
        setData(response);
      } catch (err: any) {
        setError(err.message || 'Could not load profile');
      } finally {
        setLoading(false);
      }
    };

    if (slug) fetchProfile();
  }, [slug, isPreview]);

  if (loading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '60vh' }}
      >
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error || 'Profile not found'}</Alert>
      </Container>
    );
  }

  // Destructure for cleaner access
  const { profile, compliance } = data;
  const org = profile.organisations;

  return (
    <Container className="py-5">
      {isPreview && (
        <Alert variant="info" className="mb-4">
          <strong>Preview Mode:</strong> This is how your profile appears to
          buyers.
          {profile.is_published
            ? ' This profile is currently LIVE.'
            : ' This profile is currently PRIVATE.'}
        </Alert>
      )}

      {/* Header Section */}
      <Row className="mb-5 align-items-center">
        <Col md={8}>
          {/* Updated to org.name */}
          <h1 className="display-4 fw-bold">{org.name}</h1>
          <p className="text-muted mb-2">
            Registration No: {org.registration_number}
          </p>
          {org.website && (
            <a
              href={org.website}
              target="_blank"
              rel="noreferrer"
              className="text-decoration-none"
            >
              Visit Website →
            </a>
          )}
        </Col>
        <Col md={4} className="text-md-end">
          {/* Note: JSON provided didn't have badges, adding a fallback */}
          <Badge bg="secondary" className="p-2 text-uppercase">
            {org.organisation_type}
          </Badge>
        </Col>
      </Row>

      <Row>
        {/* Left Column: Bio & Compliance Details */}
        <Col lg={8}>
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Body>
              <Card.Title className="fw-bold mb-3">
                About the Organisation
              </Card.Title>
              <Card.Text>
                {/* Updated to profile.bio */}
                {profile.bio || 'No description provided.'}
              </Card.Text>
            </Card.Body>
          </Card>

          <h4 className="mb-4 fw-bold">Detailed Compliance Performance</h4>
          <Row>
            {/* Strongest Areas */}
            <Col md={6} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <Card.Title className="text-success small fw-bold text-uppercase">
                    Strongest Areas
                  </Card.Title>
                  {compliance.section_performance.strong_sections.map(
                    (s, i) => (
                      <div key={i} className="mt-3">
                        <div className="d-flex justify-content-between mb-1">
                          <span className="small fw-semibold">{s.title}</span>
                          <span className="small">{s.average_percentage}%</span>
                        </div>
                        <AssessmentScoreBar value={s.average_percentage} />
                      </div>
                    )
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Growth Areas */}
            <Col md={6} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <Card.Title className="text-warning small fw-bold text-uppercase">
                    Growth Areas
                  </Card.Title>
                  {compliance.section_performance.weak_sections.map((s, i) => (
                    <div key={i} className="mt-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="small fw-semibold">{s.title}</span>
                        <span className="small">{s.average_percentage}%</span>
                      </div>
                      <AssessmentScoreBar value={s.average_percentage} />
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>

        {/* Right Column: KPIs */}
        <Col lg={4}>
          <Card className="bg-primary text-white border-0 shadow-sm mb-4">
            <Card.Body className="text-center py-4">
              <Card.Title className="small text-uppercase opacity-75">
                Trust Score
              </Card.Title>
              <h2 className="display-4 fw-bold mb-0">
                {compliance.average_compliance_percentage}%
              </h2>
              <p className="mb-0">Overall Compliance</p>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
              <Card.Title className="small fw-bold text-uppercase text-muted">
                Transparency
              </Card.Title>
              <div className="mt-3">
                <div className="d-flex justify-content-between mb-1 small">
                  <span>Documentation Provided</span>
                  <span>{compliance.file_overview.completion_percentage}%</span>
                </div>
                <ProgressBar
                  variant="info"
                  now={compliance.file_overview.completion_percentage}
                  style={{ height: '8px' }}
                />
              </div>
              <p className="mt-3 small text-muted">
                Based on {compliance.assessment_count} verified assessments.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SupplierProfile;
